import React, { ReactNode, useEffect, useRef, useState } from "react";
import { IdNameList } from "../../../../api/services/common/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  gridSize,
  gridSizeLongLabel,
} from "../../../../helpers/constants";
import {
  getValueOrDash,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { mdmCreateOfficeSiteCoreKeys } from "../../../mdmCreateOffice/siteCore/SiteCoreKeys";
import { OfficeDetailsResponse } from "../../modelsAndSchemas/models";
import { MdmOfficeViewModalWrapper } from "./MdmOfficeViewModal.style";

interface MdmOfficeViewModalProps {
  open: boolean;
  onClose: () => void;
  data: OfficeDetailsResponse | null;
  onOfficeEdit: (id: string) => void;
}

interface ListItem {
  label: string;
  value?: string | string[] | ReactNode;
  hidden?: boolean;
  showEllipsis?: boolean;
  render?: () => JSX.Element;
  showLongLabel?: boolean;
}

const SectionBox: React.FC<{
  header?: string;
  items: ListItem[];
  index?: number;
  moreLabel?: string;
}> = ({ header, items, index, moreLabel = "View" }) => {
  const visibleItems = items.filter((item) => !item.hidden);
  const { isMobile } = useMediaQueries();
  if (visibleItems.length === 0) return null;
  return (
    <MuiBox className="container-box">
      {header && (
        <MuiTypography variant="h2Bold" className="container-header">
          {header}
        </MuiTypography>
      )}
      <MuiGrid
        container
        spacing={isMobile ? 0 : 2}
        rowSpacing={isMobile ? 2 : 5}
        className="configuration-container"
        key={index}
      >
        {visibleItems.map((item: ListItem) => (
          <MuiGrid
            key={item.label}
            container
            size={item.showLongLabel ? gridSizeLongLabel : gridSize}
          >
            {item?.render ? (
              item?.render()
            ) : (
              <MuiDoubleLabel
                label={item.label}
                value={
                  item.showLongLabel ? (
                    <MuiViewMorePopOver
                      anchorText={moreLabel}
                      message={String(item.value)}
                      placement="bottom-end"
                    />
                  ) : (
                    item.value
                  )
                }
                replaceEmptyWithDash={true}
                isValueWithEllipsis={item.showEllipsis ?? false}
              />
            )}
          </MuiGrid>
        ))}
      </MuiGrid>
    </MuiBox>
  );
};

const MdmOfficeViewModal: React.FC<MdmOfficeViewModalProps> = ({
  open,
  onClose,
  data,
  onOfficeEdit,
}) => {
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const [officeDetails, setOfficeDetails] = useState<ListItem[][]>([]);
  const [addressAndContactDetails, setAddressAndContactDetails] = useState<
    ListItem[]
  >([]);
  const [invoiceDetails, setInvoiceDetails] = useState<ListItem[]>([]);

  const frameworkRef = useRef<HTMLDivElement>(null);

  const { commonARIALabel } = useNavigationSiteCore();

  const getLabel = (key: keyof typeof mdmCreateOfficeSiteCoreKeys) => {
    return fetchLabel(
      mdmCreateOfficeSiteCoreKeys.createNewOfficeTitle,
      mdmCreateOfficeSiteCoreKeys[key],
    );
  };

  const getChipValues = (data: IdNameList[]) => {
    return data.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  };

  const getFullAddress = (
    addressData: OfficeDetailsResponse["addressAndContactDetails"],
  ) => {
    if (!addressData) return EMPTY_VALUE_DASH;
    const parts = [
      addressData.address1,
      addressData.address2,
      addressData.address3,
      addressData.city,
      addressData.state,
      addressData.country?.name,
    ].filter(Boolean); // Remove falsy values to avoid extra commas
    let addressStr = parts.length > 0 ? parts.join(", ") : "";
    if (addressData.postalCode) {
      addressStr = addressStr
        ? `${addressStr} - ${addressData.postalCode}`
        : addressData.postalCode;
    }
    return addressStr || EMPTY_VALUE_DASH;
  };

  const populateOfficeDetails = (data: OfficeDetailsResponse | null) => {
    if (data) {
      setOfficeDetails([
        [
          {
            label: getLabel("officeName"),
            value: data.officeDetails.officeName,
          },
          {
            label: getLabel("parentOffice"),
            value: data.officeDetails.parentOffice?.name,
          },
          {
            label: getLabel("officeCode"),
            value: data.officeDetails.officeCode,
          },
          {
            label: getLabel("officeType"),
            value: fetchValueByKeyOfDropdown(
              mdmCreateOfficeSiteCoreKeys.createNewOfficeTitle,
              mdmCreateOfficeSiteCoreKeys.OfficeTypeList,
              data.officeDetails.officeType,
            ),
          },
          {
            label: getLabel("market"),
            value: data.officeDetails.market?.name,
          },
          {
            label: getLabel("searchRegion"),
            value: data.officeDetails.region?.name,
          },
          {
            label: getLabel("currency"),
            value: data.officeDetails.currency?.name,
          },
          {
            label: getLabel("invoiceTemplate"),
            value: data.officeDetails.invoiceTemplate?.name,
          },
          {
            label: getLabel("salesCreditTemplate"),
            value: data.officeDetails.salesCreditTemplate?.name,
          },
          {
            label: getLabel("defaultTax"),
            value: isNotEmpty(data.officeDetails.taxes) ? (
              <MuiChipWithPopover
                data={getChipValues(data.officeDetails.taxes)}
                isEditable={false}
                headerLabel={getLabel("defaultTax")}
                gridRef={frameworkRef}
                isDialogTitleWithEllipsis
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ) : (
              EMPTY_VALUE_DASH
            ),
          },
          {
            label: getLabel("status"),
            value: (
              <StatusBadge
                status={data.officeDetails.statusId}
                label={fetchValueByKeyOfDropdown(
                  mdmCreateOfficeSiteCoreKeys.createNewOfficeTitle,
                  mdmCreateOfficeSiteCoreKeys.createOfficeStatusList,
                  data.officeDetails.statusId,
                )}
              />
            ),
          },
        ],
        [
          {
            label: getLabel("selectedOptions"),
            value: (
              <MuiChipWithPopover
                data={getChipValues([
                  ...(data.officeDetails.isBillingOffice
                    ? [
                        {
                          id: getLabel("billingOffice"),
                          name: getLabel("billingOffice"),
                        },
                      ]
                    : []),
                  ...(data.officeDetails.isSalesCreditOffice
                    ? [
                        {
                          id: getLabel("salesCreditOffice"),
                          name: getLabel("salesCreditOffice"),
                        },
                      ]
                    : []),
                ])}
                isEditable={false}
                headerLabel={getLabel("selectedOptions")}
                gridRef={frameworkRef}
                isDialogTitleWithEllipsis
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ),
            hidden: !(
              data.officeDetails.isBillingOffice ||
              data.officeDetails.isSalesCreditOffice
            ),
          },
          {
            label: getLabel("billingOffice"),
            value: data.officeDetails.billingOffice?.name,
            hidden: !data.officeDetails.billingOffice?.name,
          },
          {
            label: getLabel("salesCreditOffice"),
            value: data.officeDetails.salesCreditOffice?.name,
            hidden: !data.officeDetails.salesCreditOffice?.name,
          },
        ],
      ]);
      setAddressAndContactDetails([
        {
          label: getLabel("address"),
          value: getFullAddress(data.addressAndContactDetails),
          showEllipsis: true,
        },
        {
          label: getLabel("businessUnit"),
          value: data.addressAndContactDetails.businessUnit?.name,
        },
        {
          label: getLabel("timeZone"),
          value: data.addressAndContactDetails.timeZone?.name,
        },
      ]);
      setInvoiceDetails([
        {
          label: getLabel("header"),
          value: getValueOrDash(data?.invoicingDetails?.header),
          showLongLabel: true,
        },
        {
          label: getLabel("footerOne"),
          value: getValueOrDash(data?.invoicingDetails?.footer1),
          showLongLabel: true,
        },
        {
          label: getLabel("footerTwo"),
          value: getValueOrDash(data?.invoicingDetails?.footer2),
          showLongLabel: true,
        },
        {
          label: getLabel("footerThree"),
          value: getValueOrDash(data?.invoicingDetails?.footer3),
          showLongLabel: true,
        },
      ]);
    } else {
      setOfficeDetails([]);
      setAddressAndContactDetails([]);
      setInvoiceDetails([]);
    }
  };

  useEffect(() => {
    populateOfficeDetails(data);
  }, [data]);

  const handleOnEditOfficeClick = () => {
    data?.id && onOfficeEdit?.(data.id);
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={replaceHashWithValue(
        getLabel("viewOfficeHeader"),
        `${data?.officeDetails.officeName ?? ""}`,
      )}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      tertiaryButtonTitle={commonLabels.edit}
      onSecondaryButtonClick={onClose}
      onTertiaryButtonClick={handleOnEditOfficeClick}
      className="mdm-office-view-modal"
    >
      <MdmOfficeViewModalWrapper>
        <AccordionHeader
          showExpandCollapseIcon={true}
          title={
            <AccordionHeaderText
              label={getLabel("officeDetailsSectionHeader")}
            />
          }
        >
          {officeDetails.map((group, idx) => (
            <SectionBox key={idx} header="" items={group} />
          ))}
        </AccordionHeader>
        {!!data?.officeDetails?.isBillingOffice && (
          <AccordionHeader
            showExpandCollapseIcon={true}
            title={
              <AccordionHeaderText label={getLabel("invoicingDetailsHeader")} />
            }
          >
            <SectionBox
              header=""
              items={invoiceDetails}
              moreLabel={commonLabels.viewMore}
            />
          </AccordionHeader>
        )}
        <AccordionHeader
          showExpandCollapseIcon={true}
          title={
            <AccordionHeaderText
              label={getLabel("addressAndContactDetailsSectionHeader")}
            />
          }
        >
          <SectionBox header="" items={addressAndContactDetails} />
        </AccordionHeader>
      </MdmOfficeViewModalWrapper>
    </MuiModal>
  );
};

export default MdmOfficeViewModal;
